import { create } from "zustand";
import { createJSONStorage, devtools, persist } from "zustand/middleware";
import posthog from "posthog-js";
import { getCurrentUser, logout } from "@/services/api/auth/auth";
import { keepRedeemCodeWhile } from "@/features/seat-orders/redeemCode";
import { useCookieConsentStore } from "@/stores/cookieConsentStore";
import type { UserSector, ScottishLocalAuthority } from "@/types/api";
import { extractStringProperty } from "@/utils/extract-string-property";

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  displayName?: string;
  profileImageUrl?: string;
  emailVerified: boolean;
  organizationId?: string;
  sector?: UserSector;
  localAuthority?: ScottishLocalAuthority;
  roles: string[];
  permissions?: string[];
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isInitialized: boolean;
  error: string | null;

  // Actions
  setUser: (user: User | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  checkAuth: () => Promise<void>;
  /**
   * Signs out and leaves for the sign-out page. Resolves true once it is leaving (even after an API failure, when it
   * still clears local data and goes home), or false when it couldn't sign out and stays on the page to retry.
   */
  logout: () => Promise<boolean>;
  clearAuth: () => void;
}

// Helper to reset PostHog user identification
const resetPostHog = () => {
  if (typeof posthog?.reset === "function") {
    try {
      posthog.reset();
    } catch (e) {
      console.warn("PostHog reset failed:", e);
    }
  }
};

export const useAuthStore = create<AuthState>()(
  devtools(
    persist(
      (set, get) => ({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        isInitialized: false,
        error: null,

        setUser: (user) => {
          const hasAnalyticsConsent = useCookieConsentStore.getState().preferences.analytics;
          if (user && hasAnalyticsConsent && typeof posthog?.identify === "function") {
            try {
              posthog.identify(user.id, {
                email: user.email,
                firstName: user.firstName,
                lastName: user.lastName,
                organizationId: user.organizationId,
              });
            } catch (e) {
              console.warn("PostHog identify failed:", e);
            }
          }
          set({
            user,
            isAuthenticated: !!user,
            error: null,
          });
        },

        setLoading: (loading) => set({ isLoading: loading }),

        setError: (error) => set({ error }),

        checkAuth: async () => {
          // Skip if already checking to prevent duplicate calls
          if (get().isLoading) return;

          set({ isLoading: true, error: null });

          try {
            const userData = await getCurrentUser();

            const isValidUser = (candidate: unknown): candidate is User => {
              if (!candidate || typeof candidate !== "object") {
                return false;
              }

              const value = candidate as Partial<User>;
              return typeof value.email === "string" && typeof value.id === "string";
            };

            if (isValidUser(userData)) {
              get().setUser(userData);
              set({ isLoading: false, isInitialized: true });
            } else {
              get().setUser(null);
              set({ isLoading: false, isInitialized: true });
            }
          } catch (error) {
            const axiosError = error as { response?: { status?: number } };
            if (axiosError?.response?.status === 401) {
              get().setUser(null);
              set({ isLoading: false, isInitialized: true });
            } else {
              console.error("Auth check failed:", error);
              get().setUser(null);
              set({
                isLoading: false,
                isInitialized: true,
                error: error instanceof Error ? error.message : "Authentication check failed",
              });
            }
          }
        },

        logout: async () => {
          set({ isLoading: true, error: null });

          // The API host cannot delete the web host's HttpOnly content cookie.
          // Do not report logout or redirect until that cookie has been cleared.
          try {
            const contentLogout = await fetch("/api/learning/scorm/content-session", {
              method: "DELETE",
              credentials: "same-origin",
            });
            if (contentLogout.status !== 204) throw new Error("Content sign-out failed");
          } catch {
            set({ isLoading: false, error: "Unable to sign out. Please try again." });
            return false;
          }

          try {
            const response = await logout();

            // Reset PostHog user identification
            resetPostHog();

            // Clear all local storage and session storage, but keep a pending seat invite code (LMS-188)
            keepRedeemCodeWhile(() => {
              localStorage.clear();
              sessionStorage.clear();
            });
            // Notify app to clear caches (e.g., React Query)
            if (typeof window !== "undefined") {
              window.dispatchEvent(new CustomEvent("app:auth-clear-cache"));
            }

            // Clear the persisted auth state
            localStorage.removeItem("auth-storage");

            set({
              user: null,
              isAuthenticated: false,
              isLoading: false,
              error: null,
            });

            // If we have a logout URL from WorkOS, redirect to it
            // The response type is Void but may contain data
            const logoutUrl = extractStringProperty(response, "logoutUrl");

            if (logoutUrl) {
              window.location.href = logoutUrl;
            } else {
              // Fallback to home page if no logout URL
              window.location.href = "/";
            }
            return true;
          } catch (error) {
            console.error("Logout failed:", error);
            set({
              isLoading: false,
              error: error instanceof Error ? error.message : "Logout failed",
            });

            // Reset PostHog user identification even on error
            resetPostHog();

            // Even on error, clear local data (keeping a pending seat invite code) and redirect
            keepRedeemCodeWhile(() => {
              localStorage.clear();
              sessionStorage.clear();
            });
            if (typeof window !== "undefined") {
              window.dispatchEvent(new CustomEvent("app:auth-clear-cache"));
            }
            window.location.href = "/";
            return true;
          }
        },

        clearAuth: () =>
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            isInitialized: false,
            error: null,
          }),
      }),
      {
        name: "auth-storage",
        storage: createJSONStorage(() => sessionStorage), // Use sessionStorage for better security
        partialize: (state) => ({
          // Only store minimal necessary data - not full user object
          isAuthenticated: state.isAuthenticated,
          // Store only non-sensitive user data if needed
          user: state.user
            ? {
                id: state.user.id,
                email: state.user.email,
                firstName: state.user.firstName,
                lastName: state.user.lastName,
                displayName: state.user.displayName,
                profileImageUrl: state.user.profileImageUrl,
                sector: state.user.sector,
                localAuthority: state.user.localAuthority,
              }
            : null,
        }),
      },
    ),
  ),
);

// Listen for unauthorized events from the API client to keep UI in sync
if (typeof window !== "undefined") {
  window.addEventListener("app:auth-unauthorized", () => {
    const { clearAuth } = useAuthStore.getState();
    clearAuth();
    // Protected query results belong to the expired session, including route-loader caches.
    window.dispatchEvent(new CustomEvent("app:auth-clear-cache"));
  });
}
